/**
 * BOOT §4 — the signed-in handoff veil (RATIFIED Direction B, "the mark docks").
 *
 * Mounted at App level (native-only) so it survives the `'/' → /today` route
 * change. When NativeRoot hands off, this paints cream + the resolved brand
 * block (pixel-identical to the celebration's end state) while `/today` mounts
 * complete and still BENEATH it — chrome arrives with the day, revealed, never
 * over the boot. Then it plays the tier's exit and unmounts:
 *
 *   COLD  → the mark FLIPs from its 80 px centered rest slot to the TopBar's
 *           32 px slot (measured, never hardcoded); wordmark fades + drifts;
 *           cream fades late so the mark lands on the revealed day. 640 ms.
 *   WARM  → veil-only fade, 280 ms.   HOT → veil-only fade, 200 ms.
 *   reduced-motion (§6) → a 160 ms opacity crossfade, no travel, any tier.
 *
 * Geometry: the brand block reuses native-root.module.css `.splash` (the D4
 * contract — 18vh / 80 px mark / 16 px gap / 52 px wordmark / short-viewport
 * branch) so the swap from NativeRoot's splash is seamless; this file adds
 * only the veil layering + exit motion.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { TayroMark } from '../brand/TayroMark';
import { useBoot } from '../../contexts/BootContext';
import { isNativeApp } from '../../lib/platform';
import rootStyles from '../../routes/native-root.module.css';
import styles from './BootVeil.module.css';

/** Exit durations (ms) per spec §3/§4 — kept here so the timers and the CSS
 *  transitions stay in lockstep. */
const DOCK_LAND_MS = 480; // COLD: mark reaches the TopBar slot → reveal it
const COLD_TOTAL_MS = 640; // COLD: cream clears (delay 140 + 320) inside this
const WARM_MS = 280;
const HOT_MS = 200;
const REDUCED_MS = 160;

export function BootVeil(): JSX.Element | null {
  const native = isNativeApp();
  const { handoff, setDocking, endHandoff } = useBoot();
  // 'rest' = brand block at the celebration's resting geometry (one frame, to
  // measure); 'exit' = transitions applied.
  const [phase, setPhase] = useState<'rest' | 'exit'>('rest');
  const markRef = useRef<HTMLSpanElement | null>(null);
  const [dockTransform, setDockTransform] = useState<string | null>(null);
  // COLD with no measurable dock target → degrade to a plain veil-only fade
  // (the spec's "plain fade" fallback) instead of a truncated dock.
  const [degraded, setDegraded] = useState(false);
  const ranRef = useRef(false);

  const mode = handoff
    ? handoff.reduced
      ? 'reduced'
      : handoff.tier === 'COLD'
        ? 'cold'
        : handoff.tier === 'WARM'
          ? 'warm'
          : 'hot'
    : null;

  const finish = useCallback(() => endHandoff(), [endHandoff]);

  useEffect(() => {
    if (!handoff || ranRef.current) return;
    ranRef.current = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let raf = 0;

    // COLD dock: measure the resting mark + the TopBar slot, one frame later
    // (give /today's TopBar a beat to lay out), then animate. If the slot
    // can't be measured (chunk not mounted yet), fall back to a plain fade so
    // the boot never hangs.
    const begin = () => {
      if (mode === 'cold') {
        const rest = markRef.current?.getBoundingClientRect();
        const dock = document
          .querySelector('[data-boot-dock]')
          ?.getBoundingClientRect();
        if (rest && dock && rest.width > 0 && dock.width > 0) {
          const scale = dock.width / rest.width;
          const tx = dock.left - rest.left;
          const ty = dock.top - rest.top;
          setDockTransform(`translate(${tx}px, ${ty}px) scale(${scale})`);
          timers.push(setTimeout(() => setDocking(false), DOCK_LAND_MS));
          timers.push(setTimeout(finish, COLD_TOTAL_MS));
        } else {
          // No dock target (e.g. destination has no TopBar) — reveal its own
          // mark and degrade to a clean veil-only fade, never a truncated dock.
          setDocking(false);
          setDegraded(true);
          timers.push(setTimeout(finish, WARM_MS));
        }
      } else {
        const dur = mode === 'warm' ? WARM_MS : mode === 'hot' ? HOT_MS : REDUCED_MS;
        timers.push(setTimeout(finish, dur));
      }
      setPhase('exit');
    };

    // Render 'rest' for one frame so the transition animates FROM the resting
    // geometry (no jump), then start the exit on the next frame.
    raf = requestAnimationFrame(begin);
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [handoff, mode, setDocking, finish]);

  // Reset for a hypothetical second handoff in the same session (defensive).
  useEffect(() => {
    if (!handoff) {
      ranRef.current = false;
      setPhase('rest');
      setDockTransform(null);
      setDegraded(false);
    }
  }, [handoff]);

  if (!native || !handoff) return null;

  const exiting = phase === 'exit';
  // Effective mode: a degraded COLD becomes a plain veil-only fade.
  const veilMode = mode === 'cold' && degraded ? 'warm' : mode;
  // HOT was a bare-cream pass-through — the veil stays bare too (no brand
  // block flash). Every other tier carries the resolved brand block down.
  const showBrand = veilMode !== 'hot';

  return (
    <div
      className={[styles.veil, exiting ? styles.exiting : ''].filter(Boolean).join(' ')}
      data-mode={veilMode}
      aria-hidden="true"
    >
      <div className={styles.cream} />
      {showBrand && (
        <div className={[rootStyles.splash, styles.brandLayer].join(' ')}>
          <span
            ref={markRef}
            className={styles.markWrap}
            style={veilMode === 'cold' && dockTransform ? { transform: dockTransform } : undefined}
          >
            <TayroMark size={80} />
          </span>
          <div className={[rootStyles.wordmark, styles.wordmark].join(' ')}>Tayro</div>
        </div>
      )}
    </div>
  );
}
